export type MeasureKind = 'distance' | 'area';

export interface MeasurePoint { x: number; y: number; z: number }

export interface MeasurementShape {
  kind: string;
  start?: MeasurePoint | null;
  end?: MeasurePoint | null;
  points?: MeasurePoint[] | null;
  distance?: number | null;
  area?: number | null;
}

const magnitude = (n: number) => (Number.isFinite(n) ? Math.abs(n) : 0);
const roundCents = (n: number) => Math.round(n * 100) / 100;

// The unit switch looks at the value as it would print, so 999.996 m reads
// "1.00 km" rather than "1000.00 m".
export const formatDistance = (metres: number): string => {
  const m = magnitude(metres);
  return roundCents(m) >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(2)} m`;
};

export const formatArea = (squareMetres: number): string => {
  const a = magnitude(squareMetres);
  return roundCents(a) >= 10000 ? `${(a / 10000).toFixed(2)} ha` : `${a.toFixed(2)} m²`;
};

export const measurementValue = (m: MeasurementShape): string =>
  m.kind === 'area' ? formatArea(m.area ?? 0) : formatDistance(m.distance ?? 0);

export const measurementLabel = (m: MeasurementShape): string =>
  `${m.kind === 'area' ? 'Area' : 'Distance'} · ${measurementValue(m)}`;

const samePosition = (a: number[], b: number[]) => a[0] === b[0] && a[1] === b[1];

// OpenLayers closes polygon rings by repeating the first vertex, and a click
// on the last vertex (the second half of a double-click) can repeat that one.
export const toMeasurementPoints = (coords: number[][]): MeasurePoint[] => {
  const kept = coords.filter((c, i) => i === 0 || !samePosition(c, coords[i - 1]));
  if (kept.length > 1 && samePosition(kept[0], kept[kept.length - 1])) kept.pop();
  return kept.map(([x, y]) => ({ x, y, z: 0 }));
};

const isFinitePoint = (p: MeasurePoint | null | undefined): p is MeasurePoint =>
  !!p && Number.isFinite(p.x) && Number.isFinite(p.y);

// Stored points are drawn as they are: a measurement taken in the Scene may
// carry height, so points that coincide in plan are still a real measurement.
export const measurementCoordinates = (m: MeasurementShape): number[][] | null => {
  if (m.kind === 'area') {
    const points = m.points ?? [];
    return points.length >= 3 && points.every(isFinitePoint) ? points.map((p) => [p.x, p.y]) : null;
  }
  return isFinitePoint(m.start) && isFinitePoint(m.end)
    ? [[m.start.x, m.start.y], [m.end.x, m.end.y]]
    : null;
};

export const coordinatesExtent = (coords: number[][]): [number, number, number, number] => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of coords) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return [minX, minY, maxX, maxY];
};

export interface OrthoGeoKeys {
  GTModelTypeGeoKey?: number;
  ProjectedCSTypeGeoKey?: number;
  GeographicTypeGeoKey?: number;
  ProjLinearUnitsGeoKey?: number;
  ProjLinearUnitSizeGeoKey?: number;
}

export type GroundFrame =
  | { kind: 'sphere' }
  | { kind: 'mercator' }
  | { kind: 'planar'; metresPerUnit: number };

const USER_DEFINED = 32767;
const WEB_MERCATOR = new Set([3857, 3785]);
const LINEAR_UNIT_METRES: Record<number, number> = { 9001: 1, 9002: 0.3048, 9003: 1200 / 3937 };
const EARTH_RADIUS = 6378137;

// Read from the COG's own GeoKeys, not from OpenLayers' guessed projection,
// which turns a user-defined projected CRS into its geographic base (degrees).
// Degrees do not scale to metres by a constant, so a geographic ortho is
// measured on the sphere. The Scene applies the same rule (orthoGroundFrame in
// public/geo-engine/geo-layers.js): both store measurements on the same file.
export const groundFrame = (keys: OrthoGeoKeys | null | undefined): GroundFrame => {
  if (!keys) return { kind: 'planar', metresPerUnit: 1 };
  const model = keys.GTModelTypeGeoKey;
  const pcs = keys.ProjectedCSTypeGeoKey;
  const projected = model === 1 || (model !== 2 && !!pcs && pcs !== USER_DEFINED);
  if (!projected) return model === 2 || keys.GeographicTypeGeoKey ? { kind: 'sphere' } : { kind: 'planar', metresPerUnit: 1 };
  if (pcs !== undefined && WEB_MERCATOR.has(pcs)) return { kind: 'mercator' };
  const unit = keys.ProjLinearUnitsGeoKey;
  const size = keys.ProjLinearUnitSizeGeoKey;
  if (unit === USER_DEFINED && size !== undefined && Number.isFinite(size) && size > 0) return { kind: 'planar', metresPerUnit: size };
  return { kind: 'planar', metresPerUnit: (unit !== undefined && LINEAR_UNIT_METRES[unit]) || 1 };
};

// Web Mercator stretches its grid by 1/cos(latitude): a grid metre at
// northing y is 1/cosh(y / R) metres on the ground, what the ScaleLine shows.
export const groundScale = (frame: GroundFrame, y: number): number => {
  if (frame.kind === 'mercator') return 1 / Math.cosh(y / EARTH_RADIUS);
  return frame.kind === 'planar' ? frame.metresPerUnit : 1;
};

export const groundLength = (coords: number[][], frame: GroundFrame): number => {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const [ax, ay] = coords[i - 1];
    const [bx, by] = coords[i];
    total += Math.hypot(bx - ax, by - ay) * groundScale(frame, (ay + by) / 2);
  }
  return total;
};

// One scale for the whole ring, taken at its mean northing: a site-sized
// polygon spans a negligible change of latitude.
export const groundArea = (ring: number[][], frame: GroundFrame): number => {
  const pts = ring.length > 1 && samePosition(ring[0], ring[ring.length - 1]) ? ring.slice(0, -1) : ring;
  if (pts.length < 3) return 0;
  const [x0, y0] = pts[0];
  let twice = 0;
  let sumY = 0;
  for (let i = 0; i < pts.length; i++) {
    const [ax, ay] = pts[i];
    const [bx, by] = pts[(i + 1) % pts.length];
    twice += (ax - x0) * (by - y0) - (bx - x0) * (ay - y0);
    sumY += ay;
  }
  const k = groundScale(frame, sumY / pts.length);
  return (Math.abs(twice) / 2) * k * k;
};
